import { Calendar, DateUtils, FeedItemDoc, FeedItemSubType, FeedUtils, PersonDoc, SubmittalCommonUtils, SubmittalFeedItemDoc, WorkflowState, WorkflowStateComplete } from 'common';
import { TimelineBubble } from '../models/Timeline';
import { h } from '@stencil/core';

/**
 * Construction of TimelineBubble presentation-model objects for the timeline UI.
 * Shared by both submittals and RFIs.
 */
export class TimelineUtils {
  /**
   * Due date for a pending workflow state, measured as working days from an anchor date.
   *
   * @param states - Full workflow states array
   * @param pendingFromIndex - Index of the first pending state (last completed index + 1)
   * @param pendingStateIndex - 0-based index within the pending states slice
   * @param anchorDate - Date to measure from (last history date, or today for drafts)
   * @param calendar - Project calendar for working-day calculations
   */
  static getPendingStateDueDate(states: WorkflowState[], pendingFromIndex: number, pendingStateIndex: number, anchorDate: Date, calendar: Calendar): Date {
    const cumulativeDays = states.slice(pendingFromIndex, pendingFromIndex + pendingStateIndex + 1).reduce((sum, state) => sum + (state.dueDays || 0), 0);
    const durationInSeconds = calendar.workingDaysToSeconds(cumulativeDays);
    return calendar.getDateFromPositiveDuration(anchorDate, durationInSeconds);
  }

  /**
   * Due date at the end of all remaining workflow states from an anchor date.
   * Returns null when there are no pending states.
   */
  static getWorkflowEndDueDate(states: WorkflowState[], pendingFromIndex: number, anchorDate: Date, calendar: Calendar): Date | null {
    const pendingStates = states.slice(pendingFromIndex);
    if (pendingStates.length === 0) return null;
    return TimelineUtils.getPendingStateDueDate(states, pendingFromIndex, pendingStates.length - 1, anchorDate, calendar);
  }

  /**
   * Builds timeline bubbles from a raw history array, workflow states, people, and a calendar.
   * Used by submittals and RFIs directly.
   *
   * @param history - The WorkflowStateComplete[] status history array
   * @param states - The workflow states for this document
   * @param people - People to resolve user_id references
   * @param calendar - Calendar instance for computing working-day due dates on pending states
   * @param createStatusId - The fallback id to use when a reversion history item has a null state_id
   */
  static getTimelineBubbles(
    history: WorkflowStateComplete[],
    states: WorkflowState[],
    people: PersonDoc[],
    calendar: Calendar,
    createStatusId: string = 'created',
  ): TimelineBubble[] {
    const lastHistoryItem = history.length > 0 ? history[history.length - 1] : null;
    const lastStateIndex = states.findIndex(s => s.id === lastHistoryItem?.state_id);

    // --- Completed (history) bubbles ---
    const historyBubbles: TimelineBubble[] = history.map(historyItem => {
      const doneDate = DateUtils.fromISO(historyItem.date);
      const user = people.find(p => p._id === historyItem.user_id);

      if (historyItem.previous_state_id) {
        // Reversion bubble: workflow was sent back to a prior state
        const prevState = states.find(s => s.id === historyItem.previous_state_id);
        const returnStateId = historyItem.state_id === null ? createStatusId : historyItem.state_id;
        const returnBubble: TimelineBubble = {
          id: returnStateId,
          dueDate: doneDate,
          doneDate: doneDate,
          people: user ? [user] : [],
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {prevState?.action}
              <ion-icon name="arrow-back-outline"></ion-icon>
            </div>
          ),
        };
        return returnBubble;
      }

      const stateIndex = states.findIndex(s => s.id === historyItem.state_id);
      const currentState = states[stateIndex];

      const bubble: TimelineBubble = {
        id: historyItem.state_id,
        dueDate: doneDate,
        doneDate: doneDate,
        people: user ? [user] : [],
        label: currentState?.pastTense ?? currentState?.action ?? '',
      };
      return bubble;
    });

    // --- Pending (future) bubbles, using working-day calendar ---
    const lastHistoryDate = DateUtils.fromISO(lastHistoryItem?.date ?? new Date().toISOString());
    const pendingFromIndex = lastStateIndex + 1;
    const pendingBubbles: TimelineBubble[] = states.slice(pendingFromIndex).map((state, index) => {
      const users = people.filter(p => state.dueBy?.includes(p._id));
      const dueDate = TimelineUtils.getPendingStateDueDate(states, pendingFromIndex, index, lastHistoryDate, calendar);
      return {
        id: state.id,
        dueDate,
        people: users || [],
        label: state.action,
      };
    });

    return [...historyBubbles, ...pendingBubbles];
  }

  /**
   * Same as {@link getTimelineBubbles}, but completed steps come from STATUS feed items
   * (`sub_id` = state id). Current/pending slices use `completedStateIndex` as the authority
   * (`undefined` = draft → treat as -1).
   */
  static getTimelineBubblesFromFeed(
    history: FeedItemDoc[],
    states: WorkflowState[],
    people: PersonDoc[],
    calendar: Calendar,
    completedStateIndex?: number,
  ): TimelineBubble[] {
    const historyBubbles: TimelineBubble[] = history.map((item, index) => {
      const doneDate = DateUtils.fromISO(item.date);
      const doneBy = people.find(p => p._id === item.user_id);
      const stateId = item.sub_id;
      const stateIndex = states.findIndex(s => s.id === stateId);
      const state = states.find(s => s.id === stateId);

      let previousState: WorkflowState | undefined;
      let dueDate: Date = doneDate;
      if (index > 0) {
        const prevItem = history[index - 1];
        const previousStateIndex = states.findIndex(s => s.id === prevItem.sub_id);
        previousState = states.find(s => s.id === prevItem.sub_id);
        dueDate = DateUtils.fromISO(prevItem.data?.dueDate ?? prevItem.date);

        if (previousStateIndex > stateIndex) {
          return {
            id: stateId,
            dueDate,
            doneDate: doneDate,
            people: doneBy ? [doneBy] : [],
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {previousState?.action ?? item.data?.action}
                <ion-icon name="arrow-back-outline"></ion-icon>
              </div>
            ),
          } as TimelineBubble;
        }
      }

      return {
        id: stateId,
        dueDate,
        doneDate: doneDate,
        people: doneBy ? [doneBy] : [],
        label: state?.pastTense ?? state?.action ?? '',
      } as TimelineBubble;
    });

    const lastStatusItem = history.length > 0 ? history[history.length - 1] : null;
    const completedIndex = completedStateIndex ?? -1;
    const currentState = completedIndex < states.length - 1 ? states[completedIndex + 1] : null;

    let anchorDate: Date = DateUtils.currentDate;
    if (currentState) {
      const lastBubbleDoneDate = lastStatusItem ? DateUtils.fromISO(lastStatusItem.date) : DateUtils.currentDate;
      const dueDate: Date = SubmittalCommonUtils.getStateDueDate(currentState, lastBubbleDoneDate, calendar);

      // If the due date is in the past, we use current date as the anchor date.
      anchorDate = new Date(Math.max(dueDate.getTime(), anchorDate.getTime()));
      historyBubbles.push({
        id: currentState.id,
        dueDate: dueDate,
        doneDate: anchorDate, // current state is not done, but this predicts the done date if its completed right now.
        people: people.filter(p => currentState.dueBy?.includes(p._id)),
        label: currentState.action,
      });
    }

    const pendingBubbles: TimelineBubble[] = states.slice(completedIndex + 2).map(state => {
      const dueDate = SubmittalCommonUtils.getStateDueDate(state, anchorDate, calendar);
      anchorDate = dueDate;
      return {
        id: state.id,
        dueDate: dueDate,
        people: people.filter(p => state.dueBy?.includes(p._id)),
        label: state.action,
      };
    });

    return [...historyBubbles, ...pendingBubbles];
  }
}
